import { SLACK_WEBHOOK_URL } from './const';
import { version } from '../../package.json';
import { ORDER_STEPS } from '../order/constants';

export function submitPageViewEventToGtm(pageName, dataObj = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'pageView',
        pageName: pageName,
        ...dataObj
    });
}

export function submitActionEventToGtm(actionType) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'actionSubmit',
        actionType: actionType
    });
}

// ---------------------------------------------------------------------
// Slack messages for the Order Online tracker.
//
// Same webhook and same call sites as before, only the look changed:
//   - normal steps: a small card (title, details, who)
//   - problems: a red card that says what happened in plain words
//   - paid: a green card with the order details
// ---------------------------------------------------------------------

const COLORS = {
    quiet: '#CBD0D6',
    warn: '#ECB22E',
    good: '#2EB67D',
    bad: '#E01E5A'
};

const ICONS = {
    start: ':arrow_right:',
    did: ':white_check_mark:',
    done: ':white_check_mark:',
    skipped: ':fast_forward:',
    exit: ':wave:',
    failed: ':x:',
    error: ':x:'
};

// What each step message says. Unknown names fall back to the step name.
const TITLES = {
    [`${ORDER_STEPS.STEP_1}|start`]: 'Step 1 · Started an order',
    [`${ORDER_STEPS.STEP_1}|did`]: 'Step 1 · File uploaded',
    [`${ORDER_STEPS.STEP_2}|did`]: 'Step 2 · File set up',
    [`${ORDER_STEPS.STEP_3}|did`]: 'Step 3 · Languages chosen',
    [`${ORDER_STEPS.STEP_4}|did`]: 'Step 4 · Information saved',
    [`${ORDER_STEPS.STEP_5}|start`]: 'Step 5 · Reached payment',
    [`${ORDER_STEPS.STEP_6}|start`]: 'Step 5 · Currency changed',
    [`${ORDER_STEPS.AFTER_PAYMENT}|did`]: 'After payment · Questions answered',
    [`${ORDER_STEPS.AFTER_PAYMENT}|skipped`]: 'After payment · Questions skipped'
};

const tidyStepName = name =>
    String(name || 'Order Online').replace(/\s+-\s+/, ' · ');

const titleOf = (stepName, status) =>
    TITLES[`${stepName}|${status}`] || tidyStepName(stepName);

// Plain words for the errors we see most. Anything else shows its code.
export function explainError(code) {
    const text = String(code || '');
    if (/INVALID_CSRF_TOKEN/i.test(text)) {
        return (
            'The security token is missing or no longer valid ' +
            '(for example, the order page was left open for too long).'
        );
    }
    if (/failed to fetch|networkerror|load failed/i.test(text)) {
        return (
            'The website could not reach the server ' +
            '(the customer’s network, or the server is down).'
        );
    }
    return null;
}

const who = userEmail => userEmail || 'guest';

const joinParts = parts => parts.filter(Boolean).join('  ·  ');

function postToSlack(payload) {
    fetch(SLACK_WEBHOOK_URL, {
        method: 'POST',
        headers: {
            Accept: 'application/json'
        },
        body: JSON.stringify(payload)
    }).then(response => {
        return response;
    });
}

// Details shown under the title (only what we have).
function detailParts({
    projectName,
    fileName,
    wordCount,
    total,
    currency,
    paymentMethod,
    note
}) {
    return [
        projectName,
        fileName,
        wordCount ? `${wordCount} words` : '',
        total ? `${total}${currency ? ` ${currency}` : ''}` : '',
        paymentMethod ? `via ${paymentMethod}` : '',
        note
    ];
}

// Small grey line under every message: who, and the full order id.
const whoLine = (userEmail, orderId) =>
    joinParts([who(userEmail), orderId ? `order ${orderId}` : '']);

const contextBlock = text => ({
    type: 'context',
    elements: [{ type: 'mrkdwn', text }]
});

// One message = one attachment, so nothing is shown twice. Three levels:
//   1. bold  : what happened
//   2. normal: the details
//   3. grey  : who and which order
export function buildSlackPayload({
    status = 'start',
    userEmail,
    orderId,
    stepName,
    requestId,
    errorCode,
    ...details
}) {
    const icon = ICONS[status] || ICONS.start;
    const title = titleOf(stepName, status);
    const fallback = `${title} · ${who(userEmail)}`;

    if (status === 'failed' || status === 'error') {
        const why = explainError(errorCode);
        const head = [`${icon} *${title} failed*`, why ? `${why}` : '']
            .filter(Boolean)
            .join('\n');
        const technical = joinParts([
            errorCode ? `Code ${errorCode}` : '',
            requestId ? `Request ${requestId}` : '',
            status === 'error' ? `App ${version}` : ''
        ]);
        const blocks = [
            { type: 'section', text: { type: 'mrkdwn', text: head } },
            contextBlock(whoLine(userEmail, orderId))
        ];
        if (technical) blocks.push(contextBlock(technical));
        if (status === 'error') blocks.push(contextBlock(navigator.userAgent));
        return {
            attachments: [
                { fallback: `${fallback} failed`, color: COLORS.bad, blocks }
            ]
        };
    }

    const extra = joinParts(detailParts(details));
    const head = `${icon} *${title}*${extra ? `\n${extra}` : ''}`;
    const color =
        status === 'skipped' || status === 'exit' ? COLORS.warn : COLORS.quiet;

    return {
        attachments: [
            {
                fallback,
                color,
                blocks: [
                    { type: 'section', text: { type: 'mrkdwn', text: head } },
                    contextBlock(whoLine(userEmail, orderId))
                ]
            }
        ]
    };
}

export function notifyToSlack(params) {
    postToSlack(buildSlackPayload(params));
}

// Paid: green card with the order details and one line per language.
export function buildPaidPayload({
    userEmail,
    orderId,
    projectName,
    fileName,
    wordCount,
    total,
    paymentMethod,
    currency,
    languages = []
}) {
    const blocks = [
        {
            type: 'section',
            text: {
                type: 'mrkdwn',
                text: `${ICONS.done} *Paid · ${projectName || 'Untitled game'}*`
            }
        },
        {
            type: 'section',
            fields: [
                { type: 'mrkdwn', text: `*File:*\n${fileName || '-'}` },
                { type: 'mrkdwn', text: `*Words:*\n${wordCount || '-'}` },
                {
                    type: 'mrkdwn',
                    text: `*Total:*\n${total || '-'}${
                        currency ? ` ${currency}` : ''
                    }`
                },
                { type: 'mrkdwn', text: `*Paid via:*\n${paymentMethod || '-'}` }
            ]
        }
    ];

    if (languages.length > 0) {
        blocks.push({
            type: 'section',
            text: { type: 'mrkdwn', text: '*Languages*' }
        });

        const languageFields = languages.map(item => ({
            type: 'mrkdwn',
            text:
                `*${item.lang}*  ${item.wordCount} words` +
                `  ·  price ${item.price}` +
                `  ·  cost ${item.cost} ${item.currency || currency || ''}`
        }));

        // Slack allows max 10 fields per section
        for (let i = 0; i < languageFields.length; i += 10) {
            blocks.push({
                type: 'section',
                fields: languageFields.slice(i, i + 10)
            });
        }
    }

    blocks.push(contextBlock(whoLine(userEmail, orderId)));

    return {
        attachments: [
            {
                fallback: `Paid · ${projectName || 'Untitled game'} · ${who(
                    userEmail
                )}`,
                color: COLORS.good,
                blocks
            }
        ]
    };
}

export function notifyJsonToSlack(params) {
    // A paid message is a card. If this is ever called with a problem
    // status, it is sent as a normal problem message instead.
    if (params.status === 'failed' || params.status === 'error') {
        postToSlack(buildSlackPayload(params));
        return;
    }
    postToSlack(buildPaidPayload(params));
}
